import { useState } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { InputText } from "primereact/inputtext";
import EmptyDataView from "~/components/nodataview";
import PermissionGate from "~/components/PermissionGate";
import CustomeButton from "~/components/Button";
import BriefcaseIcon from "../../../../../assets/images/officers.png";
import {
  DirectorAppointmentHistoryProps,
  MappedAppointments,
} from "~/Pages/Directors/types/director";
import AppointmentHistoryTable from "./AppointmentHistoryTable";
import OtherEntitiesAppointmentTable from "./OtherEntitiesAppointmentTable";
import AuditSidebar from "~/components/audit-sidebar/AuditSidebar";
import API_ROUTES_V2 from "~/api-routes-v2";

const DirectorAppointmentHistory = ({
  showModal,
  appointments,
  setEditAppointmentEntry,
  setIsEditAppointment,
  loading,
  isEditMode = false,
  showDeleteModal,
  otherEntitiesAppointments = [],
  otherEntitiesLoading = false,
  onEditOtherEntityAppointment,
  onDeleteOtherEntityAppointment,
  onFetchOtherEntitiesAppointments,
  currentEntityId,
  otherEntityDeleteMessage,
  thisEntityDeleteMessage,
  directorId,
  entityId,
}: DirectorAppointmentHistoryProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAuditSidebarVisible, setIsAuditSidebarVisible] = useState(false);
  const auditLabels = () => {
    return {
      create: "Appointment Created",
      update: "Appointment Updated",
      delete: "Appointment Deleted",
    };
  };

  const handleTabChange = (index: number) => {
    setActiveTabIndex(index);
    // Fetch other entities appointments when clicking on "Other Entities" tab (index 1)
    if (index === 1) {
      onFetchOtherEntitiesAppointments?.();
    }
  };

  const addAppointmentHandlerForThisEntity = () => {
    showModal(true, false);
  };

  const addAppointmentHandlerForOtherEntity = () => {
    showModal(true, true);
  };

  const deleteAppointmentModalHandler = () => {
    showDeleteModal?.(true);
  };

  const handleDownloadCSV = () => {
    // CSV download functionality can be implemented here
    console.log("Download CSV clicked");
  };

  // Filter appointments for "This Entity" tab - only show appointments for current entity
  const thisEntityAppointments = currentEntityId
    ? appointments.filter(
        (appointment: any) => appointment.entity_id === currentEntityId,
      )
    : appointments;

  const filteredThisEntityAppointments = thisEntityAppointments.filter(
    (appointment: MappedAppointments) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        appointment.position?.toLowerCase().includes(searchLower) ||
        appointment.role?.toLowerCase().includes(searchLower) ||
        appointment.title?.toLowerCase().includes(searchLower) ||
        appointment.status?.toLowerCase().includes(searchLower)
      );
    },
  );

  // Filter appointments for "Other Entities" tab
  const filteredOtherEntitiesAppointments = otherEntitiesAppointments.filter(
    (appointment) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        appointment.entityData?.name?.toLowerCase().includes(searchLower) ||
        appointment.position?.toLowerCase().includes(searchLower) ||
        appointment.status?.toLowerCase().includes(searchLower)
      );
    },
  );

  const renderThisEntityContent = () => {
    if (filteredThisEntityAppointments.length || loading) {
      return (
        <>
          {thisEntityDeleteMessage && (
            <div className="deletion-msg w-full mb-3">
              <span className="pi pi-times-circle mr-2 cancel-color"></span>
              <p>{thisEntityDeleteMessage}</p>
            </div>
          )}
          <AppointmentHistoryTable
            appointments={filteredThisEntityAppointments}
            addAppointmentModalHandler={addAppointmentHandlerForThisEntity}
            deleteAppointmentModalHandler={deleteAppointmentModalHandler}
            setEditAppointmentEntry={setEditAppointmentEntry}
            setIsEditAppointment={setIsEditAppointment}
            loading={loading}
            isEditMode={isEditMode}
            hideActionBar={isEditMode}
            entityId={entityId}
          />
        </>
      );
    }

    return (
      <div className="flex justify-content-center align-items-center empty-view-wrapper">
        <EmptyDataView
          Icon={
            <img
              className="users-card-icon-img"
              src={BriefcaseIcon}
              alt="Briefcase Icon"
            />
          }
          Header="No Appointments registered yet"
          Description="Begin logging all appointments for this director"
          buttonLabel="Add Appointment"
          type="button"
          buttonHandler={() => addAppointmentHandlerForThisEntity()}
          resource="director"
          action="modify"
          resourceAttr={{ entity_id: entityId }}
        />
      </div>
    );
  };

  const renderOtherEntitiesContent = () => {
    return (
      <>
        {otherEntityDeleteMessage && (
          <div className="deletion-msg w-full mb-3">
            <span className="pi pi-times-circle mr-2 cancel-color"></span>
            <p>{otherEntityDeleteMessage}</p>
          </div>
        )}
        <OtherEntitiesAppointmentTable
          appointments={filteredOtherEntitiesAppointments}
          loading={otherEntitiesLoading}
          onEditAppointment={onEditOtherEntityAppointment}
          onDeleteAppointment={onDeleteOtherEntityAppointment}
          entityId={entityId}
        />
      </>
    );
  };

  const renderSearchAndActionBar = (
    onAddClick: () => void,
    placeholder = "Search appointment by position name, role or status",
  ) => (
    <div className="flex justify-content-between align-items-center gap-3 mt-3 mb-3">
      <span className="p-input-icon-left p-input-icon-right flex-grow-1">
        <i className="pi pi-search" style={{ left: "0.75rem" }} />
        <InputText
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={placeholder}
          className="w-full"
          style={{ paddingLeft: "2.5rem", paddingRight: "2.5rem" }}
        />
        <i className="pi pi-search" style={{ right: "0.75rem" }} />
      </span>
      {
        <div className="flex gap-2">
          <PermissionGate
            resource="director"
            action="modify"
            resourceAttr={{ entity_id: entityId }}
          >
            <CustomeButton
              label="Add New"
              icon="pi pi-plus"
              iconPos="right"
              variant="contained"
              type="button"
              onClick={onAddClick}
            />
          </PermissionGate>
        </div>
      }
    </div>
  );

  // Only show tabs in edit mode (when viewing director profile)
  if (isEditMode) {
    return (
      <div className="appointment-history-container flex flex-column gap-2">
        <div className="flex justify-content-between align-items-start">
          <div className="flex flex-column gap-2">
            <p className="form-title m-0">Appointment History</p>
            <div className="form-description m-0">
              Find all positions this director has held in the entity.
            </div>
          </div>
          {activeTabIndex === 0 && (
            <PermissionGate
              resource="director"
              action="modify"
              resourceAttr={{ entity_id: entityId }}
            >
              <CustomeButton
                label="View History"
                variant="outlined"
                icon="pi pi-history mb-1 ml-2"
                iconPos="right"
                onClick={() => setIsAuditSidebarVisible?.(true)}
              />
            </PermissionGate>
          )}
        </div>

        <TabView
          activeIndex={activeTabIndex}
          onTabChange={(e) => handleTabChange(e.index)}
          className="appointment-history-tabs"
        >
          <TabPanel header="This Entity">
            {renderSearchAndActionBar(
              addAppointmentHandlerForThisEntity,
              "Search appointment by position name, role or status",
            )}
            {renderThisEntityContent()}
          </TabPanel>
          <TabPanel header="Other Entities">
            {renderSearchAndActionBar(
              addAppointmentHandlerForOtherEntity,
              "Search appointment by entity, position name, role or status",
            )}
            {renderOtherEntitiesContent()}
          </TabPanel>
        </TabView>

        {/* Audit */}
        {isAuditSidebarVisible && (
          <AuditSidebar
            heading="Appointment History"
            endpoint={API_ROUTES_V2.FETCH_DIRECTOR_APPOINTMENTS_AUDIT.replace(
              "{directorId}",
              directorId || "",
            )}
            id={entityId}
            setSidebarToggle={setIsAuditSidebarVisible}
            sidebarToggle={isAuditSidebarVisible}
            labels={auditLabels}
          />
        )}
      </div>
    );
  }

  // Onboarding flow - no tabs needed
  return (
    <div className="appointment-history-container flex flex-column gap-2">
      <div className="flex flex-column gap-2">
        <p className="form-title m-0">Appointment History</p>
        <div className="form-description m-0">
          List all the appointments this director has attended
        </div>
      </div>
      {renderThisEntityContent()}
    </div>
  );
};

export default DirectorAppointmentHistory;
